import { useEffect, useMemo, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { MessageSquare } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { api } from '@/api/api-client';
import { pickNewInboundSms, phoneSuffix, firstLine, type SmsMessageLite } from '@/services/dialer/inboundSms';

/**
 * IncomingSmsToast — la même notification que l'appel entrant, pour un SMS reçu.
 *
 * Elle disparaît d'elle-même au bout de 4 secondes : un SMS n'attend pas de
 * décision, contrairement à un appel qu'il faut décrocher ou refuser. Cliquer
 * ouvre la fiche du prospet, là où se lit la conversation.
 *
 * Le serveur est interrogé toutes les 10 secondes, comme le panel SMS. Le
 * premier passage ne fait que noter ce qui existe déjà : sans quoi ouvrir
 * l'écran ferait surgir tout l'historique d'un coup.
 */

const VISIBLE_MS = 4000;
const POLL_MS = 10000;

interface ProspectLite {
  prospect_name?: string | null;
  prospect_phone?: string | null;
  prospect_company?: string | null;
}

interface IncomingSmsToastProps {
  prospectQueue?: ProspectLite[];
  /** Ouvre la fiche du prospet, quand on a su le reconnaître. */
  onOpenProspect?: (prospect: ProspectLite) => void;
  /** Coupé quand l'utilisateur n'est pas connecté : la route répondrait 401. */
  enabled?: boolean;
}

export default function IncomingSmsToast({ prospectQueue, onOpenProspect, enabled = true }: IncomingSmsToastProps) {
  const { t } = useTranslation('dialer');
  const [current, setCurrent] = useState<SmsMessageLite | null>(null);
  const seenRef = useRef<Set<string>>(new Set());
  const primedRef = useRef(false);

  const { data: messages } = useQuery({
    queryKey: ['inbound-sms-toast'],
    queryFn: async () => {
      const res = await api.functions.invoke('listInboundSms', { limit: 20 });
      const data = (res?.data ?? {}) as { messages?: SmsMessageLite[] };
      return data.messages || [];
    },
    refetchInterval: POLL_MS,
    enabled,
  });

  useEffect(() => {
    if (!messages) return;
    if (!primedRef.current) {
      // Premier passage : on enregistre l'existant sans rien annoncer.
      for (const m of messages) if (m.id) seenRef.current.add(String(m.id));
      primedRef.current = true;
      return;
    }
    const fresh = pickNewInboundSms(messages, seenRef.current);
    for (const m of messages) if (m.id) seenRef.current.add(String(m.id));
    if (fresh) setCurrent(fresh);
  }, [messages]);

  useEffect(() => {
    if (!current) return undefined;
    const timer = window.setTimeout(() => setCurrent(null), VISIBLE_MS);
    return () => window.clearTimeout(timer);
  }, [current]);

  const matchedProspect = useMemo(() => {
    if (!current || !prospectQueue) return null;
    const suffix = phoneSuffix(current.prospect_phone || current.from);
    if (!suffix) return null;
    return prospectQueue.find((p) => phoneSuffix(p.prospect_phone) === suffix) || null;
  }, [current, prospectQueue]);

  const displayName = matchedProspect?.prospect_name || t('dialer:incomingSms.unknownSender');
  const displayPhone = current?.prospect_phone || current?.from || '';

  return (
    <AnimatePresence>
      {current && (
        <motion.div
          key="incoming-sms-toast"
          initial={{ opacity: 0, y: 20, scale: 0.96 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: 20, scale: 0.96 }}
          transition={{ type: 'spring', stiffness: 360, damping: 28 }}
          className="fixed z-[7400] pointer-events-auto"
          // Au-dessus de la barre d'appel du bas, et sous le toast d'appel
          // entrant : un appel qui sonne passe devant un SMS reçu.
          style={{ right: 16, bottom: 64, width: 360 }}
          role="status"
          aria-live="polite"
        >
          <div
            className="rounded-2xl shadow-2xl overflow-hidden"
            style={{ background: '#111216', border: '1px solid rgba(255,255,255,0.08)' }}
          >
            <div
              className={`flex items-center gap-3 px-3.5 py-3 transition-colors ${matchedProspect ? 'cursor-pointer hover:bg-white/5' : ''}`}
              onClick={() => { if (matchedProspect) { onOpenProspect?.(matchedProspect); setCurrent(null); } }}
              role={matchedProspect ? 'button' : undefined}
              tabIndex={matchedProspect ? 0 : undefined}
              onKeyDown={(e) => {
                if (matchedProspect && (e.key === 'Enter' || e.key === ' ')) {
                  e.preventDefault();
                  onOpenProspect?.(matchedProspect);
                  setCurrent(null);
                }
              }}
            >
              <span
                className="relative flex items-center justify-center rounded-full flex-shrink-0"
                style={{ width: 38, height: 38, background: 'rgba(96, 165, 250, 0.18)', color: '#60A5FA' }}
              >
                <MessageSquare className="w-4 h-4" />
              </span>

              {/* 🚨 Kevin, 2026-09-30 : « le prenom et le nom et la premiere
                  ligne ». Rien d'autre. La societe, le numero et le libelle
                  « recu » disaient ce que l'icone et la fiche disent deja, et
                  repoussaient le message hors de vue. */}
              <div className="flex-1 min-w-0" data-testid="sms-entrant-infos">
                <p className="text-[13px] font-semibold text-white truncate">{displayName}</p>
                <p className="text-[12px] text-white/85 truncate">{firstLine(current.body)}</p>
              </div>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
